import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/api/client";
import { Countdown } from "@/components/Countdown";
import { getWMStart, isBonusLocked, BONUS_MAX_POINTS } from "@/lib/bonus";
import { WM_GROUPS } from "@/lib/constants";
import { Lock, Sparkles, CheckCircle2 } from "lucide-react";

type Variant = "hero" | "compact";

export function BonusBanner({ variant = "compact" }: { variant?: Variant }) {
  const { user } = useAuth();
  const [hasAny, setHasAny] = useState<boolean | null>(null);
  const [points, setPoints] = useState(0);
  const [wmStart, setWmStart] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.bonus.me().catch(() => null),
      getWMStart(),
    ]).then(([d, start]: [any, any]) => {
      const gw = d?.groupWinners ?? {};
      const allGroupsFilled = WM_GROUPS.every((k) => gw[k] && String(gw[k]).trim().length > 0);
      const filled = !!(d && d.worldChampion && d.topScorer && allGroupsFilled);
      setHasAny(filled);
      setPoints(d?.pointsEarned ?? 0);
      setWmStart(start);
    });
  }, [user]);

  if (!user || hasAny === null) return null;
  const locked = isBonusLocked(wmStart);

  // Locked state
  if (locked) {
    return (
      <Link to="/bonus" className="block rounded-lg p-4 bg-muted border border-border tap">
        <div className="flex items-center gap-3">
          <Lock className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">🔒 Bonus-Fragen gesperrt</div>
            <div className="text-[11px] text-muted-foreground">WM hat begonnen · Aktuell: +{points} Pkt</div>
          </div>
          <span className="text-xs text-muted-foreground">Ansehen →</span>
        </div>
      </Link>
    );
  }

  // Hero (Profile)
  if (variant === "hero") {
    return (
      <Link to="/bonus" className={`block rounded-lg shadow-pop p-4 tap ${hasAny ? "bg-card border-2 border-accent" : "bg-accent text-accent-foreground"}`}>
        <div className="flex items-center gap-3">
          <div className="text-3xl shrink-0">{hasAny ? "🌍" : "🎯"}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">
              {hasAny ? "Deine Bonus-Fragen" : "Jetzt sichern!"}
            </div>
            <div className="font-display font-extrabold text-lg leading-tight">
              {hasAny ? `Bonus-Fragen bearbeiten →` : `Deine Bonus-Fragen abgeben`}
            </div>
            <div className="text-xs mt-1 opacity-90">
              {hasAny ? <>Aktuell <b>+{points} Pkt</b> · bis zu +{BONUS_MAX_POINTS}</> : <>bis zu <b>+{BONUS_MAX_POINTS} Punkte</b> sichern</>}
              {wmStart && <> · Sperre in <Countdown target={wmStart} compact /></>}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Compact (Dashboard)
  if (!hasAny) {
    return (
      <Link to="/bonus" className="block rounded-lg shadow-pop p-4 bg-accent text-accent-foreground tap">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-base leading-tight">⚠️ Deine Bonus-Fragen offen</div>
            <div className="text-xs mt-0.5 opacity-90">
              Bis zu <b>+{BONUS_MAX_POINTS} Pkt</b>
              {wmStart && <> · Sperre in <Countdown target={wmStart} compact /></>}
            </div>
          </div>
          <span className="text-sm font-bold">→</span>
        </div>
      </Link>
    );
  }
  return (
    <Link to="/bonus" className="block rounded-lg shadow-card p-3 bg-card border border-border tap">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight">✅ Bonus-Fragen abgegeben</div>
          <div className="text-[11px] text-muted-foreground">
            +{points} Pkt sicher · bis zu +{BONUS_MAX_POINTS} möglich
            {wmStart && <> · Sperre in <Countdown target={wmStart} compact /></>}
          </div>
        </div>
        <span className="text-xs text-muted-foreground">Bearbeiten →</span>
      </div>
    </Link>
  );
}
